import { runInAction } from 'mobx';
import { ApplicationRoutes } from '../../../../../application/constants/routes';
import { ILocalization, ILocalizationToken } from '../../../../../application/language/ILocalization';
import { inject } from '../../../../../common/container/inject';
import { sleep } from '../../../../../common/data/fake/sleep';
import { IApplicationModel } from '../../../../../service/application/entity/IApplicationModel';
import { ILearningRootService, LearningRootServiceToken } from '../../../../../service/LearningRootService';
import { injectRootService } from '../../../../../service/RootServiceFactory';
import { IRouterService, RouterServiceToken } from '../../../../../service/route/IRouterService';
import { IUnitCardModel } from '../../../../../service/unit/interface/IUnitCardModel';
import { IUnitResultModel } from '../../../../../service/unitResult/IUnitResultModel';
import { UserDataLevel } from '../../../../../service/userData/IUserDataModel';
import { LayoutNotificationType } from '../../../../layout/common/notification/store/ILayoutNotification';
import { IMainLayoutDomainStore } from '../../../../layout/main/store/domain/IMainLayoutDomainStore';
import { IProjectView } from '../../../../user/page/project/info/store/model/IProjectView';
import { LANGUAGES_MAP } from '../parts/components/UnitCardLangs';
import { IPassingListPageDomain } from './IPassingListPageDomain';
import { IPassingListPageUI } from './IPassingListPageUI';
import { PassingListPageUI } from './PassingListPageUI';

export class PassingListPageDomain implements IPassingListPageDomain {
  public ui: IPassingListPageUI;
  constructor(
    public layoutDomain: IMainLayoutDomainStore,
    protected router = inject<IRouterService>(RouterServiceToken),
    protected learningRootService = inject<ILearningRootService>(LearningRootServiceToken),
    protected rootService = injectRootService(layoutDomain.serviceType.value),
    public i18n: ILocalization = inject<ILocalization>(ILocalizationToken),
  ) {
    this.ui = new PassingListPageUI();
  }
  loadImportantUnits?: ((projects: IProjectView[]) => void) | undefined;
  loadData = async (userId?: string) => {
    const params = new URLSearchParams(window.location.search);
    const initialQuery = params.get('q') || '';

    this.ui.searchText.setValue(initialQuery);

    if (this.ui.isLoading.value) {
      return;
    }

    this.ui.isLoading.setValue(true);
    const id = userId ? userId : this.layoutDomain.ui.activeUser.entity.id;
    const unitResults = await this.learningRootService.unitResult
      .search({
        limit: 100000,
        filter: [{ fieldName: 'userId', type: 'equal', value: id }],
      })
      .catch((err) => {
        if (err.webCode === '500') {
          this.layoutDomain.notifications.showNotification({
            type: LayoutNotificationType.error,
            text: this.i18n.translate('phrases.somethingWentWrong'),
          });
          return { data: [] };
        }
        return { data: [] };
      });

    const projects = (
      await this.rootService.project.entity.search({
        limit: 100000,
        filter: {
          json: [
            {
              fieldPath: 'rolesMap.data[].userId',
              value: id,
              type: 'equal',
            },
          ],
        },
      })
    ).data;

    const userProjectIds = projects.map((project) => project.id);
    const userProjectRoles = projects.flatMap((project) => (project.rolesMap?.data ?? []).map((role) => role.roleId));

    const allUsersApplications = (
      await this.rootService.application.entity.search({
        limit: 100000,
        filter: { projectId: { in: userProjectIds as string[] } },
      })
    ).data;

    const allUnits = await this.learningRootService.unit.getListCards({ limit: 100000 });
    const publishedUnits = allUnits.filter((unit) => unit.isPublished);

    const importantUserUnits = this.determineMandatoryUnits(
      publishedUnits,
      allUsersApplications,
      projects,
      userProjectRoles,
      id,
    );

    const unimportantUnitList = publishedUnits.filter((unit) => !importantUserUnits.includes(unit));

    const combinedUnits = [...importantUserUnits, ...unimportantUnitList];
    runInAction(() => {
      this.ui.importantUnitList.setList(importantUserUnits);
      this.ui.unimportantUnitList.setList(unimportantUnitList);
      this.ui.passingList.setList(combinedUnits);
    });

    const { data: specificationCategoryData } = await this.rootService.specification.category.search({
      fields: ['name', 'id'],
      limit: 100000,
    });
    const { data: characteristicData } = await this.rootService.specification.entity.search({
      fields: ['name', 'categoryId'],
      limit: 10000,
    });
    runInAction(() => {
      this.ui.specificationCategory.setList(specificationCategoryData);
      this.ui.characteristic.setList(characteristicData);
    });

    const userUnitResults = (
      await this.learningRootService.unitResult.search({
        limit: 100000,
        filter: [{ fieldName: 'userId', type: 'equal', value: id }],
      })
    ).data.filter((unitResult) => {
      return combinedUnits.some((unit) => unit.id === unitResult.unitId);
    });

    runInAction(() => {
      this.ui.passingReslutList.setList(userUnitResults);
    });
    this.getAllPossibleScoreOfUnits();

    this.createdGroupedList();
    this.checkingPassedAmount();
    if (userUnitResults) {
      setTimeout(() => {
        this.ui.isLoading.setValue(false);
      }, 300);
    }
    this.ui.filtredGroupedUnits.setList(this.ui.groupedUnits.list);

    if (initialQuery) {
      this.handleSearchByGroupsAndName(initialQuery, { updateUrl: false });
    }

    const currentUser = this.layoutDomain.ui.activeUser.entity;

    const applicationsWithUnits = this.getSystemsByUnits(importantUserUnits, allUsersApplications)
      .map((application) => {
        const applicationCopy: any = {
          id: application.id,
          name: application.name,
          specificationsIds: application.specificationsIds,
        };
        const specificationsIds = application.specificationsIds || [];

        const systemProject = projects.find((item) => application.projectId == item.id);
        const projectRole = systemProject?.rolesMap?.data?.find((item) => item.userId === currentUser.id)?.roleId;
        applicationCopy.units = importantUserUnits.filter((unit) => {
          return (
            unit.settings.teamRolesIds.includes(projectRole) &&
            unit.settings.characteristicsIds.some((characteristicId) => specificationsIds.includes(characteristicId))
          );
        });

        applicationCopy.unitResults = applicationCopy.units.map((unit) => {
          return this.ui.passingReslutList.list.find((result) => result.unitId === unit.id) || null;
        });

        return applicationCopy;
      })
      .filter((application) => {
        return application.units.length;
      });

    this.ui.applicationsWithUnits.setList(applicationsWithUnits);
    if (unitResults?.data) {
      this.getUserData(id, combinedUnits, unitResults.data);
    }
  };

  getUserDataById = async (id: string) => {
    const userDataSearch = await this.learningRootService.userData.search({
      limit: 100000,
      fields: { include: ['toNextLevelScore', 'level', 'totalScore', 'maxScore', 'levelThresholds'] },
      filter: [
        {
          fieldName: 'userId',
          type: 'equal',
          value: id,
        },
      ],
    });

    return userDataSearch.data.length > 0 ? userDataSearch.data[0] : null;
  };

  getUserData = async (id: string, combinedUnits: IUnitCardModel[], userUnitResults: IUnitResultModel[]) => {
    let userData = await this.getUserDataById(id);
    if (!userData) {
      try {
        userData = await this.createNewUserDate(id, combinedUnits, userUnitResults);
      } catch {
        // can be race condition
        await sleep(300);
        userData = await this.getUserDataById(id);
      }
    }
    if (!userData) return;
    this.ui.userData.setValue(userData);
  };

  createNewUserDate = async (id: string, combinedUnits: IUnitCardModel[], userUnitResults: IUnitResultModel[]) => {
    let newMaxScore = 0;
    let newUserTotalScore = 0;
    combinedUnits.forEach((unit) => (newMaxScore += unit.score));
    userUnitResults.forEach((reuslt) => (newUserTotalScore += reuslt.totalUnitResult));
    const level =
      newUserTotalScore <= Math.round((newMaxScore * 35) / 100)
        ? UserDataLevel.JUNIOR
        : Math.round((newMaxScore * 35) / 100) <= newUserTotalScore &&
          newUserTotalScore <= Math.round((newMaxScore * 69) / 100)
        ? UserDataLevel.MIDDLE
        : UserDataLevel.SENIOR;

    const toNextLevelScore =
      level === UserDataLevel.SENIOR
        ? Math.round(newMaxScore - newUserTotalScore)
        : level === UserDataLevel.MIDDLE
        ? Math.round((newMaxScore * 69) / 100 - newUserTotalScore + 1)
        : Math.round((newMaxScore * 35) / 100 - newUserTotalScore + 1);

    const levelThresholds = {
      junior: Math.round((newMaxScore * 35) / 100),
      middle: Math.round((newMaxScore * 69) / 100),
      senior: newMaxScore,
    };

    const newUserDataId = await this.learningRootService.userData.createByModel({
      userId: id,
      totalScore: newUserTotalScore,
      level,
      maxScore: newMaxScore,
      toNextLevelScore,
      levelThresholds,
    });
    const newUserDataSearchResult = await this.learningRootService.userData.getById(newUserDataId);

    return newUserDataSearchResult;
  };

  getAllPossibleScoreOfUnits = () => {
    let score = 0;
    this.ui.passingList.list.forEach((unit) => {
      score += unit.score;
    });
    this.ui.allUnitsScore.setValue({ allUnitsScore: score, possibleScoreOnePart: Math.round(score / 3) });
  };

  checkingPassedAmount = () => {
    this.ui.groupedUnits.list.forEach((group) => {
      group.importantUnitList.forEach((unit) => {
        for (let i = 0; i < this.ui.passingReslutList.list.length; i++) {
          if (unit.id === this.ui.passingReslutList.list[i].unitId && this.ui.passingReslutList.list[i].isPassed) {
            group.passedAmount += 1;
          }
        }
      });
    });
    this.ui.groupedUnits.list.forEach((group) => {
      group.unimportantUnitList.forEach((unit) => {
        for (let i = 0; i < this.ui.passingReslutList.list.length; i++) {
          if (unit.id === this.ui.passingReslutList.list[i].unitId && this.ui.passingReslutList.list[i].isPassed) {
            group.passedAmount += 1;
          }
        }
      });
    });
  };

  createdGroupedList = () => {
    const characteristicToSpecName = {};
    this.ui.characteristic.list.forEach((char) => {
      const spec = this.ui.specificationCategory.list.find((spec) => spec.id === char.categoryId);
      if (spec && char?.id) {
        characteristicToSpecName[char.id] = spec.name;
      }
    });

    const groupedUnits = {};
    const allUnits = [...this.ui.importantUnitList.list, ...this.ui.unimportantUnitList.list];

    allUnits.forEach((unit) => {
      const charIds = unit.settings.characteristicsIds;
      if (charIds && charIds.length > 0) {
        const charId = charIds[0];
        const groupName = characteristicToSpecName[charId] || charId;
        groupedUnits[groupName] = groupedUnits[groupName] || [];
        groupedUnits[groupName].push(unit);
      } else {
        const groupName = this.i18n.translate('learningAdminPage.withoutContext');
        groupedUnits[groupName] = groupedUnits[groupName] || [];
        groupedUnits[groupName].push(unit);
      }
    });

    const sortedGroupNames = Object.keys(groupedUnits).sort();

    this.ui.groupedUnits.setList(
      sortedGroupNames.map((groupName) => ({
        groupName,
        importantUnitList: groupedUnits[groupName].filter((unit) => this.ui.importantUnitList.list.includes(unit)),
        unimportantUnitList: groupedUnits[groupName].filter((unit) => !this.ui.importantUnitList.list.includes(unit)),
        passedAmount: 0,
        passingPercent: 0,
      })),
    );
    this.calculatePassingPercentages();
  };

  calculatePassingPercentages = () => {
    this.ui.groupedUnits.list.forEach((group) => {
      const totalUnits = group.importantUnitList.length + group.unimportantUnitList.length;
      const passedUnits = this.calculatePassedUnits(group);
      group.passingPercent = Math.round((passedUnits / totalUnits) * 100);
    });
  };

  calculatePassedUnits = (group) => {
    let passedAmount = 0;
    [...group.importantUnitList, ...group.unimportantUnitList].forEach((unit) => {
      if (this.ui.passingReslutList.list.some((result) => result.unitId === unit.id && result.isPassed)) {
        passedAmount++;
      }
    });
    return passedAmount;
  };

  redirect = async (id: string) => {
    if (this.ui.isAdminMode.value) return;
    const currentSearch = window.location.search;
    const redirectBack = `${ApplicationRoutes.LearningRoot}${currentSearch}`;
    this.router.goTo(
      `${ApplicationRoutes.LearningUnitPassing.replace(':passingId', id)}?redirectBack=${encodeURIComponent(
        redirectBack,
      )}`,
    );
  };

  handleSearchByGroupsAndName = (searchText: string, options: { updateUrl?: boolean } = { updateUrl: true }) => {
    const normalize = (str: string | undefined | null): string =>
      (str ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ''); // только буквы и цифры

    const trimmedOriginal = searchText.toLowerCase();
    const trimmed = normalize(searchText);

    this.ui.searchText.setValue(searchText);

    // --- Обновление URL
    if (options.updateUrl) {
      const params = new URLSearchParams(window.location.search);
      trimmedOriginal ? params.set('q', trimmedOriginal) : params.delete('q');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }

    if (!trimmed) {
      this.ui.filtredGroupedUnits.setList(this.ui.groupedUnits.list);
      this.calculatePassingPercentages();
      return;
    }

    const getCweStrictSearch = () => {
      const match = trimmedOriginal.match(/^cwe[\s\-_]*([0-9]+)$/i);
      if (!match) return null;

      const number = match[1];
      return `cwe${number}`;
    };

    const unitMatches = (unit) => {
      const { titleHeading, titleLead, description } = unit.settings;

      const normalizedHeading = normalize(titleHeading);
      const normalizedLead = normalize(titleLead);
      const normalizedDesc = normalize(description);

      const fullSearchText = normalizedHeading + normalizedLead + normalizedDesc;

      // --- Строгий cwe поиск
      const cweStrictSearch = getCweStrictSearch();
      if (cweStrictSearch) {
        const cweTokens = normalizedDesc.match(/cwe[0-9]+/g) ?? [];

        const cweMatch = cweTokens.includes(cweStrictSearch);
        if (cweMatch) return true;

        return false;
      }

      // --- Обычный нестрогий поиск
      const languages = unit.practice?.languages ?? [];
      const languagesMatch = languages.some((code) => {
        const meta = LANGUAGES_MAP[code];
        if (!meta) return false;

        return code.toLowerCase().includes(trimmedOriginal) || meta.title.toLowerCase().includes(trimmedOriginal);
      });

      return languagesMatch || fullSearchText.includes(trimmed);
    };

    const filteredGroups = this.ui.groupedUnits.list
      .map((group) => {
        const groupMatches = normalize(group.groupName).includes(trimmed);

        const filterList = (list) => list.filter((unit) => unitMatches(unit));

        return {
          ...group,
          importantUnitList: groupMatches ? group.importantUnitList : filterList(group.importantUnitList),
          unimportantUnitList: groupMatches ? group.unimportantUnitList : filterList(group.unimportantUnitList),
        };
      })
      .filter((group) => group.importantUnitList.length > 0 || group.unimportantUnitList.length > 0);

    this.ui.filtredGroupedUnits.setList(filteredGroups);
    this.calculatePassingPercentages();
  };

  determineMandatoryUnits = (units, applications, projects, userProjectRoles, userId) => {
    return units.filter((unit) => {
      return applications.some((application) => {
        // Проверяем, связан ли проект пользователя с приложением
        const userProject = projects.find((project) => project.id === application.projectId);

        // Проверяем, имеет ли пользователь в проекте роль, совпадающую с ролью в настройках юнита
        const isUserInProjectRole = userProject.rolesMap.data.some(
          (role) => role.userId === userId && userProjectRoles.includes(role.roleId),
        );

        // Проверяем, соответствует ли роль пользователя в проекте какой-либо из ролей, необходимых для юнита
        const hasMatchingRole = unit.settings.teamRolesIds.some((unitRole) =>
          userProject.rolesMap.data.some(
            (projectRole) => projectRole.userId === userId && projectRole.roleId === unitRole,
          ),
        );

        // Проверяем совпадение характеристик юнита и приложения
        const hasMatchingCharacteristic = unit.settings.characteristicsIds.some((characteristic) =>
          application.specificationsIds.includes(characteristic),
        );

        return isUserInProjectRole && hasMatchingRole && hasMatchingCharacteristic;
      });
    });
  };

  getSystemsByUnits = (importantUserUnits, allUsersApplications) => {
    const systems: IApplicationModel[] = [];
    importantUserUnits.forEach((unit) => {
      allUsersApplications.forEach((item) => {
        if (
          unit.settings.characteristicsIds.some((characteristic) => item.specificationsIds?.includes(characteristic)) &&
          !systems.some((system) => system.id === item.id)
        ) {
          systems.push(item);
        }
      });
    });

    return systems;
  };
}
